"use strict";

import _classCallCheck from "/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import { getAddress, getContractAddress } from "@ethersproject/address";
import { BigNumber } from "@ethersproject/bignumber";
import { hexDataLength, hexDataSlice, hexValue, hexZeroPad, isHexString } from "@ethersproject/bytes";
import { AddressZero } from "@ethersproject/constants";
import { shallowCopy } from "@ethersproject/properties";
import { parse as parseTransaction } from "@ethersproject/transactions";
import { Logger } from "@ethersproject/logger";
import { version } from "./_version";
var logger = new Logger(version);
export var Formatter = /*#__PURE__*/function () {
  function Formatter() {
    _classCallCheck(this, Formatter);

    logger.checkNew(this instanceof Formatter ? this.constructor : void 0, Formatter);
    this.formats = this.getDefaultFormats();
  }

  _createClass(Formatter, [{
    key: "getDefaultFormats",
    value: function getDefaultFormats() {
      var _this = this;

      var formats = {};
      var address = this.address.bind(this);
      var bigNumber = this.bigNumber.bind(this);
      var blockTag = this.blockTag.bind(this);
      var data = this.data.bind(this);
      var hash = this.hash.bind(this);
      var hex = this.hex.bind(this);
      var number = this.number.bind(this);

      var strictData = function strictData(v) {
        return _this.data(v, true);
      };

      formats.transaction = {
        hash: hash,
        blockHash: Formatter.allowNull(hash, null),
        blockNumber: Formatter.allowNull(number, null),
        transactionIndex: Formatter.allowNull(number, null),
        confirmations: Formatter.allowNull(number, null),
        from: address,
        gasPrice: bigNumber,
        gasLimit: bigNumber,
        to: Formatter.allowNull(address, null),
        value: bigNumber,
        nonce: number,
        data: data,
        r: Formatter.allowNull(this.uint256),
        s: Formatter.allowNull(this.uint256),
        v: Formatter.allowNull(number),
        creates: Formatter.allowNull(address, null),
        raw: Formatter.allowNull(data)
      };
      formats.transactionRequest = {
        from: Formatter.allowNull(address),
        nonce: Formatter.allowNull(number),
        gasLimit: Formatter.allowNull(bigNumber),
        gasPrice: Formatter.allowNull(bigNumber),
        to: Formatter.allowNull(address),
        value: Formatter.allowNull(bigNumber),
        data: Formatter.allowNull(strictData)
      };
      formats.receiptLog = {
        transactionIndex: number,
        blockNumber: number,
        transactionHash: hash,
        address: address,
        topics: Formatter.arrayOf(hash),
        data: data,
        logIndex: number,
        blockHash: hash
      };
      formats.receipt = {
        to: Formatter.allowNull(this.address, null),
        from: Formatter.allowNull(this.address, null),
        contractAddress: Formatter.allowNull(address, null),
        transactionIndex: number,
        root: Formatter.allowNull(hash),
        gasUsed: bigNumber,
        logsBloom: Formatter.allowNull(data),
        blockHash: hash,
        transactionHash: hash,
        logs: Formatter.arrayOf(this.receiptLog.bind(this)),
        blockNumber: number,
        confirmations: Formatter.allowNull(number, null),
        cumulativeGasUsed: bigNumber,
        status: Formatter.allowNull(number)
      };
      formats.block = {
        hash: hash,
        parentHash: hash,
        number: number,
        timestamp: number,
        nonce: Formatter.allowNull(hex),
        difficulty: this.difficulty.bind(this),
        gasLimit: bigNumber,
        gasUsed: bigNumber,
        miner: address,
        extraData: data,
        transactions: Formatter.allowNull(Formatter.arrayOf(hash))
      };
      formats.blockWithTransactions = shallowCopy(formats.block);
      formats.blockWithTransactions.transactions = Formatter.allowNull(Formatter.arrayOf(this.transactionResponse.bind(this)));
      formats.filter = {
        fromBlock: Formatter.allowNull(blockTag, undefined),
        toBlock: Formatter.allowNull(blockTag, undefined),
        blockHash: Formatter.allowNull(hash, undefined),
        address: Formatter.allowNull(address, undefined),
        topics: Formatter.allowNull(this.topics.bind(this), undefined)
      };
      formats.filterLog = {
        blockNumber: Formatter.allowNull(number),
        blockHash: Formatter.allowNull(hash),
        transactionIndex: number,
        removed: Formatter.allowNull(this.boolean.bind(this)),
        address: address,
        data: Formatter.allowFalsish(data, "0x"),
        topics: Formatter.arrayOf(hash),
        transactionHash: hash,
        logIndex: number
      };
      return formats;
    } // Requires a BigNumberish that is within the IEEE754 safe integer range; returns a number
    // Strict! Used on input.

  }, {
    key: "number",
    value: function number(_number) {
      return BigNumber.from(_number).toNumber();
    } // Strict! Used on input.

  }, {
    key: "bigNumber",
    value: function bigNumber(value) {
      return BigNumber.from(value);
    } // Requires a boolean, "true" or  "false"; returns a boolean

  }, {
    key: "boolean",
    value: function boolean(value) {
      if (typeof value === "boolean") {
        return value;
      }

      if (typeof value === "string") {
        value = value.toLowerCase();

        if (value === "true") {
          return true;
        }

        if (value === "false") {
          return false;
        }
      }

      throw new Error("invalid boolean - " + value);
    }
  }, {
    key: "hex",
    value: function hex(value, strict) {
      if (typeof value === "string") {
        if (!strict && value.substring(0, 2) !== "0x") {
          value = "0x" + value;
        }

        if (isHexString(value)) {
          return value.toLowerCase();
        }
      }

      return logger.throwArgumentError("invalid hash", "value", value);
    }
  }, {
    key: "data",
    value: function data(value, strict) {
      var result = this.hex(value, strict);

      if (result.length % 2 !== 0) {
        throw new Error("invalid data; odd-length - " + value);
      }

      return result;
    } // Requires an address
    // Strict! Used on input.

  }, {
    key: "address",
    value: function address(value) {
      return getAddress(value);
    }
  }, {
    key: "callAddress",
    value: function callAddress(value) {
      if (!isHexString(value, 32)) {
        return null;
      }

      var address = getAddress(hexDataSlice(value, 12));
      return address === AddressZero ? null : address;
    }
  }, {
    key: "contractAddress",
    value: function contractAddress(value) {
      return getContractAddress(value);
    } // Strict! Used on input.

  }, {
    key: "blockTag",
    value: function blockTag(_blockTag) {
      if (_blockTag == null) {
        return "latest";
      }

      if (_blockTag === "earliest") {
        return "0x0";
      }

      if (_blockTag === "latest" || _blockTag === "pending") {
        return _blockTag;
      }

      if (typeof _blockTag === "number" || isHexString(_blockTag)) {
        return hexValue(_blockTag);
      }

      throw new Error("invalid blockTag");
    } // Requires a hash, optionally requires 0x prefix; returns prefixed lowercase hash.

  }, {
    key: "hash",
    value: function hash(value, strict) {
      var result = this.hex(value, strict);

      if (hexDataLength(result) !== 32) {
        return logger.throwArgumentError("invalid hash", "value", value);
      }

      return result;
    } // Returns the difficulty as a number, or if too large (i.e. PoA network) null

  }, {
    key: "difficulty",
    value: function difficulty(value) {
      if (value == null) {
        return null;
      }

      var v = BigNumber.from(value);

      try {
        return v.toNumber();
      } catch (error) {}

      return null;
    }
  }, {
    key: "uint256",
    value: function uint256(value) {
      if (!isHexString(value)) {
        throw new Error("invalid uint256");
      }

      return hexZeroPad(value, 32);
    }
  }, {
    key: "_block",
    value: function _block(value, format) {
      if (value.author != null && value.miner == null) {
        value.miner = value.author;
      }

      return Formatter.check(format, value);
    }
  }, {
    key: "block",
    value: function block(value) {
      return this._block(value, this.formats.block);
    }
  }, {
    key: "blockWithTransactions",
    value: function blockWithTransactions(value) {
      return this._block(value, this.formats.blockWithTransactions);
    } // Strict! Used on input.

  }, {
    key: "transactionRequest",
    value: function transactionRequest(value) {
      return Formatter.check(this.formats.transactionRequest, value);
    }
  }, {
    key: "transactionResponse",
    value: function transactionResponse(transaction) {
      // Rename gas to gasLimit
      if (transaction.gas != null && transaction.gasLimit == null) {
        transaction.gasLimit = transaction.gas;
      } // Some clients (TestRPC) do strange things like return 0x0 for the
      // 0 address; correct this to be a real address


      if (transaction.to && BigNumber.from(transaction.to).isZero()) {
        transaction.to = "0x0000000000000000000000000000000000000000";
      } // Rename input to data


      if (transaction.input != null && transaction.data == null) {
        transaction.data = transaction.input;
      } // If to and creates are empty, populate the creates from the transaction


      if (transaction.to == null && transaction.creates == null) {
        transaction.creates = this.contractAddress(transaction);
      } // @TODO: use transaction.serialize? Have to add support for including v, r, and s...

      /*
      if (!transaction.raw) {
             // Very loose providers (e.g. TestRPC) do not provide a signature or raw
           if (transaction.v && transaction.r && transaction.s) {
               let raw = [
                   stripZeros(hexlify(transaction.nonce)),
                   stripZeros(hexlify(transaction.gasPrice)),
                   stripZeros(hexlify(transaction.gasLimit)),
                   (transaction.to || "0x"),
                   stripZeros(hexlify(transaction.value || "0x")),
                   hexlify(transaction.data || "0x"),
                   stripZeros(hexlify(transaction.v || "0x")),
                   stripZeros(hexlify(transaction.r)),
                   stripZeros(hexlify(transaction.s)),
               ];
                 transaction.raw = rlpEncode(raw);
           }
       }
       */


      var result = Formatter.check(this.formats.transaction, transaction);

      if (transaction.chainId != null) {
        var chainId = transaction.chainId;

        if (isHexString(chainId)) {
          chainId = BigNumber.from(chainId).toNumber();
        }

        result.chainId = chainId;
      } else {
        var _chainId = transaction.networkId; // geth-etc returns chainId

        if (_chainId == null && result.v == null) {
          _chainId = transaction.chainId;
        }

        if (isHexString(_chainId)) {
          _chainId = BigNumber.from(_chainId).toNumber();
        }

        if (typeof _chainId !== "number" && result.v != null) {
          _chainId = (result.v - 35) / 2;

          if (_chainId < 0) {
            _chainId = 0;
          }

          _chainId = parseInt(_chainId);
        }

        if (typeof _chainId !== "number") {
          _chainId = 0;
        }

        result.chainId = _chainId;
      } // 0x0000... should actually be null


      if (result.blockHash && result.blockHash.replace(/0/g, "") === "x") {
        result.blockHash = null;
      }

      return result;
    }
  }, {
    key: "transaction",
    value: function transaction(value) {
      return parseTransaction(value);
    }
  }, {
    key: "receiptLog",
    value: function receiptLog(value) {
      return Formatter.check(this.formats.receiptLog, value);
    }
  }, {
    key: "receipt",
    value: function receipt(value) {
      var result = Formatter.check(this.formats.receipt, value);

      if (value.status != null) {
        result.byzantium = true;
      }

      return result;
    }
  }, {
    key: "topics",
    value: function topics(value) {
      var _this2 = this;

      if (Array.isArray(value)) {
        return value.map(function (v) {
          return _this2.topics(v);
        });
      } else if (value != null) {
        return this.hash(value, true);
      }

      return null;
    }
  }, {
    key: "filter",
    value: function filter(value) {
      return Formatter.check(this.formats.filter, value);
    }
  }, {
    key: "filterLog",
    value: function filterLog(value) {
      return Formatter.check(this.formats.filterLog, value);
    }
  }], [{
    key: "check",
    value: function check(format, object) {
      var result = {};

      for (var key in format) {
        try {
          var value = format[key](object[key]);

          if (value !== undefined) {
            result[key] = value;
          }
        } catch (error) {
          error.checkKey = key;
          error.checkValue = object[key];
          throw error;
        }
      }

      return result;
    } // if value is null-ish, nullValue is returned

  }, {
    key: "allowNull",
    value: function allowNull(format, nullValue) {
      return function (value) {
        if (value == null) {
          return nullValue;
        }

        return format(value);
      };
    } // If value is false-ish, replaceValue is returned

  }, {
    key: "allowFalsish",
    value: function allowFalsish(format, replaceValue) {
      return function (value) {
        if (!value) {
          return replaceValue;
        }

        return format(value);
      };
    } // Requires an Array satisfying check

  }, {
    key: "arrayOf",
    value: function arrayOf(format) {
      return function (array) {
        if (!Array.isArray(array)) {
          throw new Error("not an array");
        }

        var result = [];
        array.forEach(function (value) {
          result.push(format(value));
        });
        return result;
      };
    }
  }]);

  return Formatter;
}(); // Show the throttle message only once

var throttleMessage = false;
export function showThrottleMessage() {
  if (throttleMessage) {
    return;
  }

  throttleMessage = true;
  console.log("========= NOTICE =========");
  console.log("Request-Rate Exceeded  (this message will not be repeated)");
  console.log("");
  console.log("The default API keys for each service are provided as a highly-throttled,");
  console.log("community resource for low-traffic projects and early prototyping.");
  console.log("");
  console.log("While your application will continue to function, we highly recommended");
  console.log("signing up for your own API keys to improve performance, increase your");
  console.log("request rate/limit and enable other perks, such as metrics and advanced APIs.");
  console.log("");
  console.log("For more details: https:/\/docs.ethers.io/api-keys/");
  console.log("==========================");
}